import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '../../data.service';
declare var $: any;

@Component({
  selector: 'app-internal-assessment',
  templateUrl: './internal-assessment.component.html',
  styleUrls: ['./internal-assessment.component.scss']
})
export class InternalAssessmentComponent implements OnInit {

  constructor(private router: Router, private route: ActivatedRoute, private dataService: DataService) { }

  batchId: any = 0;
  subjectId: any = 0;
  
  data: any = { list: [], count: 0 };
  limit: any = 50;
  offset: any = 0;
  loading: any = false;
  search: any = "";
  batch: any = { course: { name: '' }, start_year: '', end_year: '', current_semester: '' };
  subject: any = { topic: '' };

  // IA Configuration
  iaConfig: any[] = [];
  iaScores: any = {}; // studentId => { componentId => { attempt1, attempt2, ..., avg, scaled } }
  savingScores: any = {}; // Track which cells are being saved

  ngOnInit(): void {
    this.batchId = this.route.snapshot.paramMap.get('batchId');
    this.subjectId = this.route.snapshot.paramMap.get('subjectId');
    this.loadBatch();
    this.loadSubject();
    this.loadConfiguration();
  }

  loadBatch() {
    this.dataService.getBatch(this.batchId).subscribe((data: any) => {
      if (data.status == 0) {
        this.batch = data;
      }
    });
  }

  loadSubject() {
    this.dataService.getSubjectInfo(this.subjectId).subscribe((data: any) => {
      if (data.status == 0) {
        this.subject = data.data;
      }
    });
  }

  loadConfiguration() {
    this.loading = true;
    this.dataService.getIAConfig(this.subjectId).subscribe((res: any) => {
      if (res.status == 0) {
        this.iaConfig = res.data;
        this.loadStudents();
      } else {
        this.loading = false;
      }
    });
  }

  loadStudents() {
    this.dataService.getStudents(this.limit, this.offset, this.search, this.batchId).subscribe((data: any) => {
      if (data.status == 0) {
        this.data = data;
        this.loadAllScores();
      }
    });
  }

  loadAllScores() {
    if (this.data.list.length == 0) {
      this.loading = false;
      return;
    }

    const studentIds = this.data.list.map((s: any) => s.id).join(',');
    this.dataService.getIAScores(this.subjectId, studentIds).subscribe((res: any) => {
      this.loading = false;
      if (res.status == 0) {
        this.processScores(res.data);
      }
    });
  }

  processScores(scores: any[]) {
    // Initialize score structure for all students and components
    this.data.list.forEach((student: any) => {
      this.iaScores[student.id] = {};
      
      this.iaConfig.forEach((comp: any) => {
        this.iaScores[student.id][comp.id] = {
          attempts: {},
          avg: 0,
          scaled: 0
        };
        
        // Initialize all attempt slots
        for (let i = 1; i <= comp.number_of_attempts; i++) {
          this.iaScores[student.id][comp.id].attempts[i] = null;
        }
      });
    });

    // Fill in existing scores
    scores.forEach((score: any) => {
      if (!this.iaScores[score.student_id]) return;
      if (!this.iaScores[score.student_id][score.component_id]) return;
      
      this.iaScores[score.student_id][score.component_id].attempts[score.attempt_number] = parseFloat(score.marks_obtained);
      
      // Recalculate for this component
      this.calculateComponentScores(score.student_id, score.component_id);
    });

    // Calculate final scores
    this.calculateFinalScores();
  }

  calculateComponentScores(studentId: any, componentId: any) {
    const component = this.iaConfig.find(c => c.id == componentId);
    if (!component) return;

    const scoreData = this.iaScores[studentId][componentId];
    const attemptValues = Object.values(scoreData.attempts)
      .filter((v: any) => v !== null && v !== undefined && v !== '')
      .map((v: any) => parseFloat(v));

    if (attemptValues.length == 0) {
      scoreData.avg = 0;
      scoreData.scaled = 0;
      return;
    }

    // Sort descending and take best N
    attemptValues.sort((a, b) => b - a);
    const bestAttempts = attemptValues.slice(0, component.best_of_attempts);
    
    // Calculate average
    const sum = bestAttempts.reduce((acc, val) => acc + val, 0);
    scoreData.avg = sum / bestAttempts.length;
    
    // Calculate scaled
    scoreData.scaled = (scoreData.avg / component.base_marks) * component.scaled_marks;
  }

  calculateFinalScores() {
    this.data.list.forEach((student: any) => {
      let total = 0;
      this.iaConfig.forEach((comp: any) => {
        if (this.iaScores[student.id] && this.iaScores[student.id][comp.id]) {
          total += this.iaScores[student.id][comp.id].scaled || 0;
        }
      });
      student.ia_total = total.toFixed(2);
    });
  }

  onScoreChange(studentId: any, componentId: any, attemptNumber: any, value: any) {
    const component = this.iaConfig.find(c => c.id == componentId);
    if (!component) return;

    // Validate
    if (value && parseFloat(value) > component.base_marks) {
      alert(`Marks cannot exceed ${component.base_marks}`);
      this.iaScores[studentId][componentId].attempts[attemptNumber] = null;
      return;
    }

    // Update score
    this.iaScores[studentId][componentId].attempts[attemptNumber] = value ? parseFloat(value) : null;
    
    // Recalculate
    this.calculateComponentScores(studentId, componentId);
    this.calculateFinalScores();

    // Save to backend
    this.saveScore(studentId, componentId, attemptNumber, value);
  }

  saveScore(studentId: any, componentId: any, attemptNumber: any, value: any) {
    const key = `${studentId}-${componentId}-${attemptNumber}`;
    this.savingScores[key] = true;

    const data = {
      student_id: studentId,
      batch_id: this.batchId,
      subject_id: this.subjectId,
      component_id: componentId,
      attempt_number: attemptNumber,
      marks_obtained: value || 0
    };

    console.log('Saving score:', data);

    this.dataService.saveIAScore(data).subscribe(
      (res: any) => {
        this.savingScores[key] = false;
        console.log('Save response:', res);
        if (res.status != 0) {
          alert('Error saving score: ' + res.msg);
        }
      },
      (error: any) => {
        this.savingScores[key] = false;
        console.error('Save error:', error);
        alert('Network error while saving score');
      }
    );
  }

  openConfigModal() {
    //@ts-ignore
    $('#modal-ia-config').modal('show');
  }

  onConfigSaved() {
    this.loadConfiguration();
  }

  paginate(dir: any) {
    if (dir == 2) {
      this.offset += this.limit;
    } else {
      this.offset -= this.limit;
      if (this.offset < 0) {
        this.offset = 0;
      }
    }

    this.loadStudents();
  }

  getAttemptArray(count: number): number[] {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  getTotalColspan(): number {
    let total = 2; // Student ID + Name
    this.iaConfig.forEach((comp: any) => {
      total += comp.number_of_attempts + 2; // attempts + avg + scaled
    });
    total += 1; // Final total
    return total;
  }

}
